import React from 'react';
import { Shield } from 'lucide-react';
import LegalPageLayout from '../components/legal/LegalPageLayout';
import LegalLink from '../components/legal/LegalLink';
import { legalBodyText, legalLinkClass, legalSectionTitle } from '../components/legal/legalStyles';

const PrivacyPage: React.FC = () => {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      lastUpdated="June 2026"
      icon={<Shield className="w-6 h-6 text-white" />}
      iconGradientClassName="from-green-600 to-emerald-600"
      cardTitle="Edyt Privacy Policy"
      defaultBack={{ href: '/signup', label: 'Back to Sign Up' }}
      footer={{
        href: '/signup',
        label: 'Back to Sign Up',
        buttonGradientClassName:
          'from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700',
      }}
    >
      <div className={legalBodyText}>
        <section>
          <h2 className={legalSectionTitle}>1. Information We Collect</h2>
          <p>We collect information you provide directly to us, such as:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li><strong>Account Information:</strong> Name, email address, company name, and password</li>
            <li><strong>Integration Data:</strong> Data from connected services like Slack (messages, channels, user information)</li>
            <li><strong>Usage Information:</strong> How you interact with our service, including features used and time spent</li>
            <li><strong>Technical Information:</strong> IP address, browser type, device information, and log data</li>
          </ul>
        </section>

        <section>
          <h2 className={legalSectionTitle}>2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Provide, maintain, and improve our service</li>
            <li>Process transactions and send related information</li>
            <li>Send technical notices, updates, and administrative messages</li>
            <li>Analyze customer communications to provide insights and recommendations</li>
            <li>Detect, prevent, and address technical issues and security threats</li>
          </ul>
        </section>

        <section>
          <h2 className={legalSectionTitle}>3. Data Security and Multi-Tenancy</h2>
          <p>We implement robust security measures to protect your data:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li><strong>Multi-Tenant Isolation:</strong> Your data is completely isolated from other customers</li>
            <li><strong>Encryption:</strong> Data is encrypted in transit and at rest</li>
            <li><strong>Access Controls:</strong> Strict role-based access controls within your organization</li>
            <li><strong>Regular Security Audits:</strong> We regularly review and update our security practices</li>
          </ul>
        </section>

        <section>
          <h2 className={legalSectionTitle}>4. Data Sharing and Disclosure</h2>
          <p>We do not sell, rent, or share your personal information with third parties, except:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>With your explicit consent</li>
            <li>To comply with legal obligations or court orders</li>
            <li>To protect our rights, privacy, safety, or property</li>
            <li>With service providers who assist us in operating our service (under strict confidentiality agreements)</li>
          </ul>
        </section>

        <section>
          <h2 className={legalSectionTitle}>5. Integration Data Handling</h2>
          <p>When you connect third-party services like Slack:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>We only access data you explicitly authorize through OAuth flows</li>
            <li>Integration data is processed to provide customer insights and analytics</li>
            <li>You can disconnect integrations at any time from Integrations; disconnect removes stored integration data and credentials</li>
            <li>Historical data is deleted when you disconnect an integration or use Data Management in Settings</li>
          </ul>
        </section>

        <section>
          <h2 className={legalSectionTitle}>6. Data Retention</h2>
          <p>
            We retain your information for as long as your account is active or as needed to provide
            services. You can delete integration data by disconnecting integrations, delete specific
            data categories (messages, accounts) from Settings → Data Management, or delete all
            organization data while preserving basic tenant info. You may also contact us at{' '}
            <a href="mailto:beni_l@edytio.com" className={legalLinkClass}>
              beni_l@edytio.com
            </a>
            .
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>7. Your Rights and Choices</h2>
          <p>You have the right to:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Access, update, or delete your personal information</li>
            <li>Disconnect integrations and erase associated stored data</li>
            <li>
              Export your organization data in machine-readable JSON from Settings → Data Management
              using &quot;Download my data&quot; (GDPR Articles 15 and 20)
            </li>
            <li>Object to processing of your personal information</li>
            <li>Request restriction of processing under certain circumstances</li>
          </ul>
          <p className="mt-3">
            Business customers may review our{' '}
            <LegalLink to="/dpa">Data Processing Agreement</LegalLink>.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>8. Subprocessors</h2>
          <p>
            We use third-party subprocessors to host, secure, and deliver the service (e.g. cloud
            infrastructure, vector storage, billing). A current list with purposes and vendor links
            is available on our <LegalLink to="/subprocessors">Subprocessors page</LegalLink>.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>9. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your own.
            We ensure appropriate safeguards are in place to protect your information in accordance
            with applicable data protection laws.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>10. Children&apos;s Privacy</h2>
          <p>
            Our service is not intended for children under 13 years of age. We do not knowingly
            collect personal information from children under 13.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>11. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes
            by posting the new Privacy Policy on this page and updating the &quot;Last updated&quot; date.
          </p>
        </section>

        <section>
          <h2 className={legalSectionTitle}>12. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our data practices, please contact us at:
          </p>
          <div className="mt-2">
            <p>
              Email:{' '}
              <a href="mailto:beni_l@edytio.com" className={legalLinkClass}>
                beni_l@edytio.com
              </a>
            </p>
            <p>Address: Tel Aviv, Israel</p>
          </div>
        </section>
      </div>
    </LegalPageLayout>
  );
};

export default PrivacyPage;
